import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input, PrimaryBtn, GhostBtn, Modal } from "@/components/admin/ui";
import { ScanLine, Plus, Trash2, Save, Loader2, Lock, Eye, EyeOff, Camera, Images, ArrowLeft, X } from "lucide-react";

const SESSION_LABEL = { gara: "Gara", prove_libere: "Prove Libere", qualifica: "Qualifica" };
const normName = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

export default function ResultsEditor({ event, onClose, onBack, initialSession }) {
  const sessions = event.session_types && event.session_types.length ? event.session_types : [event.session_type || "gara"];
  const [activeSession, setActiveSession] = useState(initialSession && sessions.includes(initialSession) ? initialSession : sessions[0]);
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [driverNames, setDriverNames] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [champState, setChampState] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosOpen, setPhotosOpen] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const { isAdmin } = useAuth();
  const LOCKED_STATES = ["iscrizione_chiusa", "in_corso", "terminata"];
  const locked = !isAdmin && (LOCKED_STATES.includes(event.state) || LOCKED_STATES.includes(champState));

  const enrolledSet = new Set(enrolled.map((e) => normName(e.name)).filter(Boolean));
  const hasEnrolled = enrolledSet.size > 0;
  const isEnrolled = (name) => !hasEnrolled || enrolledSet.has(normName(name));

  // Conteggio nomi per evidenziare i doppioni
  const nameCounts = {};
  rows.forEach((r) => { const k = normName(r.name); if (k) nameCounts[k] = (nameCounts[k] || 0) + 1; });
  const hasDuplicates = Object.values(nameCounts).some((c) => c > 1);

  useEffect(() => {
    if (event.championship_id) {
      api.get(`/championships/${event.championship_id}/drivers`).then((r) => setDriverNames(r.data)).catch(() => {});
      api.get(`/championships/${event.championship_id}`).then((r) => setChampState(r.data.state)).catch(() => {});
      api.get(`/championships/${event.championship_id}/enrollments`).then((r) => setEnrolled(r.data)).catch(() => {});
    }
  }, [event.championship_id]);

  useEffect(() => {
    setLoaded(false);
    setLoadError(false);
    api.get(`/events/${event.id}/results?session=${activeSession}`).then((r) => {
      setRows(r.data.map((x) => ({
        position: x.position, name: x.name, time: x.time || "",
        penalty_seconds: x.penalty_seconds || 0, penalty_positions: x.penalty_positions || 0, hidden: false,
      })));
      setLoaded(true);
    }).catch(() => { setLoadError(true); setLoaded(true); });
  }, [event.id, activeSession]);

  const loadPhotos = () => api.get(`/events/${event.id}/result-photos`).then((r) => setPhotos(r.data)).catch(() => {});

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setParsing(true);
    try {
      const parsed = [];
      for (const file of files) {
        // Archivia la foto (consultabile fino a fine campionato)
        try {
          const afd = new FormData();
          afd.append("file", file);
          afd.append("session", activeSession);
          await api.post(`/events/${event.id}/result-photos`, afd);
        } catch (_) { /* archiviazione non bloccante */ }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/parse-scoreboard", fd);
        for (const r of (data.rows || [])) {
          const nm = (r.name || "").trim();
          if (nm) parsed.push({ position: r.position, name: nm, time: r.time || "" });
        }
      }
      let added = 0, dups = 0;
      setRows((prev) => {
        added = 0; dups = 0;
        const seen = new Set(prev.map((r) => normName(r.name)));
        const merged = [...prev];
        for (const p of parsed) {
          const key = normName(p.name);
          if (seen.has(key)) { dups++; continue; }
          seen.add(key);
          merged.push({ position: p.position || (merged.length + 1), name: p.name, time: p.time, penalty_seconds: 0, penalty_positions: 0, hidden: false });
          added++;
        }
        merged.sort((a, b) => (Number(a.position) || 999) - (Number(b.position) || 999));
        return merged;
      });
      if (added === 0 && dups === 0) toast.error("Nessun pilota riconosciuto nelle foto");
      else if (added === 0) toast(`Tutti i piloti erano già presenti · ${dups} ignorati`);
      else toast.success(`${added} piloti aggiunti${dups ? ` · ${dups} già presenti ignorati` : ""}`);
      loadPhotos();
    } catch (err) {
      toast.error("Impossibile analizzare le foto");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const setRow = (i, k, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [k]: (k === "name" || k === "time") ? v : parseInt(v || 0, 10) };
    setRows(next);
  };
  const toggleHidden = (i) => {
    const next = [...rows];
    next[i] = { ...next[i], hidden: !next[i].hidden };
    setRows(next);
  };
  const addRow = () => setRows([...rows, { position: rows.length + 1, name: "", time: "", penalty_seconds: 0, penalty_positions: 0, hidden: false }]);
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const openPhotos = () => { setPhotosOpen(true); loadPhotos(); };
  const deletePhoto = async (id) => {
    try {
      await api.delete(`/result-photos/${id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (_) { toast.error("Errore eliminazione foto"); }
  };

  const save = async () => {
    const clean = rows.filter((r) => r.name.trim() && !r.hidden);
    setSaving(true);
    try {
      await api.post("/results", { event_id: event.id, session: activeSession, rows: clean });
      toast.success(`Risultati ${SESSION_LABEL[activeSession]} salvati`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const title = (
    <span className="flex items-center gap-2">
      {onBack && (
        <button onClick={onBack} data-testid="results-back-btn" className="text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      Risultati · {event.name}
    </span>
  );

  return (
    <Modal title={title} onClose={onClose} testid="results-editor">
      <div className="space-y-4">
        <datalist id="driver-names">
          {driverNames.map((n) => <option key={n} value={n} />)}
        </datalist>

        {sessions.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="results-session-tabs">
            {sessions.map((s) => (
              <button key={s} type="button" data-testid={`results-session-${s}`} onClick={() => setActiveSession(s)}
                className={`px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase border transition-colors duration-200 ${
                  activeSession === s ? "border-yellow-400 bg-yellow-400/10 text-yellow-400" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}>
                {SESSION_LABEL[s] || s}
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-zinc-500">
          Stai modificando la classifica di <strong className="text-zinc-300">{SESSION_LABEL[activeSession] || activeSession}</strong>.
          {activeSession === "prove_libere" && " (le prove libere non assegnano punti)"}
          {activeSession === "qualifica" && " (la qualifica assegna punti solo se abilitata nel campionato)"}
        </p>

        {locked && (
          <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs font-bold px-4 py-3 rounded" data-testid="results-locked-note">
            <Lock className="h-4 w-4" /> Iscrizioni chiuse: la gara è chiusa, in corso o terminata. Solo l'amministratore può aggiungere o modificare i piloti.
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid="scoreboard-file" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" multiple onChange={onFile} className="hidden" data-testid="scoreboard-camera" />
          <PrimaryBtn onClick={() => fileRef.current?.click()} disabled={parsing || locked || !loaded} data-testid="upload-scoreboard-btn">
            {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
            {parsing ? "Analisi foto…" : "Carica foto (AI)"}
          </PrimaryBtn>
          <GhostBtn onClick={() => camRef.current?.click()} disabled={parsing || locked || !loaded} data-testid="camera-scoreboard-btn">
            <Camera className="h-4 w-4" /> Scatta foto
          </GhostBtn>
          <GhostBtn onClick={openPhotos} data-testid="uploaded-photos-btn">
            <Images className="h-4 w-4" /> Foto caricate
          </GhostBtn>
          <GhostBtn onClick={addRow} disabled={locked || !loaded} data-testid="add-row-btn"><Plus className="h-4 w-4" /> Riga</GhostBtn>
        </div>
        <p className="text-xs text-zinc-500">
          Puoi caricare o <strong>scattare più foto</strong> del tabellone per includere tutti i piloti: l'AI riconosce posizioni, nomi e tempi senza doppioni. Le foto restano in <strong>archivio</strong> (tasto "Foto caricate") fino a fine campionato.
        </p>
        {hasDuplicates && (
          <div className="flex items-center gap-2 border border-red-500/40 bg-red-500/10 text-red-300 text-xs font-bold px-4 py-3 rounded" data-testid="results-dup-note">
            ⚠ Attenzione: ci sono <strong>nomi uguali</strong> ripetuti (evidenziati in rosso). Controlla e correggi prima di salvare.
          </div>
        )}
        {hasEnrolled && (
          <p className="text-[11px] text-amber-400/80" data-testid="results-notenrolled-legend">
            I piloti in <span className="text-amber-400 font-bold">giallo</span> non risultano iscritti al campionato: puoi oscurarli momentaneamente (non verranno salvati) o ripristinarli.
          </p>
        )}
        {loadError && (
          <p className="text-xs text-red-400" data-testid="results-load-error">
            Impossibile caricare i risultati esistenti. Il salvataggio è disabilitato per evitare perdite di dati. Riprova più tardi.
          </p>
        )}

        <div className="border border-zinc-800">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-950 text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">
            <div className="col-span-2">Pos</div>
            <div className="col-span-3">Pilota</div>
            <div className="col-span-2">Tempo</div>
            <div className="col-span-2">Pen. sec</div>
            <div className="col-span-2">Pen. pos</div>
            <div className="col-span-1"></div>
          </div>
          {!loaded ? (
            <p className="px-3 py-6 text-center text-zinc-500 text-sm" data-testid="results-loading">Caricamento risultati…</p>
          ) : rows.length === 0 ? (
            <p className="px-3 py-6 text-center text-zinc-600 text-sm">Nessuna riga. Carica una foto o aggiungi manualmente.</p>
          ) : rows.map((r, i) => {
            const notEnrolled = r.name.trim() && !isEnrolled(r.name);
            const isDup = r.name.trim() && nameCounts[normName(r.name)] > 1;
            const rowCls = r.hidden
              ? "opacity-40 bg-zinc-950"
              : isDup ? "bg-red-500/10 border-l-2 border-red-500"
              : notEnrolled ? "bg-amber-500/10 border-l-2 border-amber-500" : "";
            return (
              <div key={i} data-testid={`result-row-${i}`} className={`grid grid-cols-12 gap-2 px-3 py-2 border-t border-zinc-800 items-center ${rowCls}`}>
                <Input type="number" data-testid={`row-pos-${i}`} value={r.position} onChange={(e) => setRow(i, "position", e.target.value)} className="col-span-2 px-2" />
                <Input data-testid={`row-name-${i}`} list="driver-names" value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} className="col-span-3" />
                <Input data-testid={`row-time-${i}`} value={r.time} onChange={(e) => setRow(i, "time", e.target.value)} className="col-span-2 px-2" placeholder="1:23.4" />
                <Input type="number" data-testid={`row-pensec-${i}`} value={r.penalty_seconds} onChange={(e) => setRow(i, "penalty_seconds", e.target.value)} className="col-span-2 px-2" />
                <Input type="number" data-testid={`row-penpos-${i}`} value={r.penalty_positions} onChange={(e) => setRow(i, "penalty_positions", e.target.value)} className="col-span-2 px-2" />
                <div className="col-span-1 flex justify-end gap-1">
                  {notEnrolled && (
                    <button onClick={() => toggleHidden(i)} data-testid={`hide-row-${i}`} title={r.hidden ? "Ripristina" : "Oscura"}
                      className={`transition-colors duration-200 p-1.5 ${r.hidden ? "text-zinc-500 hover:text-green-400" : "text-amber-400 hover:text-amber-300"}`}>
                      {r.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  )}
                  <button onClick={() => delRow(i)} data-testid={`del-row-${i}`}
                    className="text-zinc-500 hover:text-red-400 transition-colors duration-200 p-1.5">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3">
          {onBack && <GhostBtn onClick={onBack} data-testid="results-back-footer"><ArrowLeft className="h-4 w-4" /> Indietro</GhostBtn>}
          <GhostBtn onClick={onClose}>Chiudi</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving || loadError || locked || !loaded} data-testid="save-results-btn">
            <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : `Salva ${SESSION_LABEL[activeSession] || ""}`}
          </PrimaryBtn>
        </div>
      </div>

      {photosOpen && (
        <Modal title="Foto caricate" onClose={() => setPhotosOpen(false)} testid="uploaded-photos-modal">
          <div className="space-y-4">
            <p className="text-xs text-zinc-500">Foto del tabellone caricate per questo Gran Premio. Restano disponibili fino a quando il campionato non passa a "Terminata".</p>
            {photos.length === 0 ? (
              <p className="text-zinc-600 py-8 text-center text-sm" data-testid="no-uploaded-photos">Nessuna foto caricata.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="uploaded-photos-grid">
                {photos.map((p) => (
                  <div key={p.id} className="relative group border border-zinc-800">
                    <a href={`${API}/result-photos/file/${p.id}`} target="_blank" rel="noreferrer">
                      <img src={`${API}/result-photos/file/${p.id}`} alt="" className="w-full h-32 object-cover" />
                    </a>
                    <span className="absolute bottom-1 left-1 bg-zinc-950/80 text-[9px] font-bold uppercase tracking-wider text-cyan-400 px-1.5 py-0.5">
                      {SESSION_LABEL[p.session] || p.session}
                    </span>
                    <button onClick={() => deletePhoto(p.id)} data-testid={`del-photo-${p.id}`}
                      className="absolute top-1 right-1 h-7 w-7 flex items-center justify-center rounded-full bg-zinc-950/80 text-zinc-300 hover:text-red-400 transition-colors duration-200">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </Modal>
  );
}
